
import { businessProgress } from '@/integrations/supabase/client';
import { supabase } from '@/integrations/supabase/client';

export interface BusinessProgress {
  id: string;
  business_name: string;
  development_stage: "development" | "launch" | "growth";
  description: string | null;
  industry_type: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
}

class BusinessProgressService {
  async getBusinessProgress(): Promise<BusinessProgress[]> {
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get the current user's profile to check if they're admin
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', user?.id)
        .single();
      
      const isAdmin = profile?.user_type === 'admin';
      
      // Build query based on user role
      let query = businessProgress().select('*');
      
      // If not admin, only get their own business progress
      if (!isAdmin && user) {
        query = query.eq('user_id', user.id);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      // Map the data to ensure correct types
      const progress = data.map(item => ({
        id: item.id,
        business_name: item.business_name,
        development_stage: item.development_stage as "development" | "launch" | "growth",
        description: item.description,
        industry_type: item.industry_type as string | null,
        user_id: item.user_id,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));
      
      return progress;
    } catch (error) {
      console.error('Error fetching business progress:', error);
      throw error;
    }
  }
  
  async createBusinessProgress(data: Omit<BusinessProgress, "created_at" | "id" | "updated_at">): Promise<BusinessProgress> {
    try {
      // Get the current user if user_id is not provided
      if (!data.user_id) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        data.user_id = user.id;
      }
      
      console.log('Creating business progress with data:', data);
      
      const { data: newProgress, error } = await businessProgress()
        .insert(data)
        .select()
        .single();
      
      if (error) {
        console.error('Error details:', error);
        throw error;
      }
      
      return {
        id: newProgress.id,
        business_name: newProgress.business_name,
        development_stage: newProgress.development_stage as "development" | "launch" | "growth",
        description: newProgress.description,
        industry_type: newProgress.industry_type as string | null,
        user_id: newProgress.user_id,
        created_at: newProgress.created_at,
        updated_at: newProgress.updated_at
      };
    } catch (error) {
      console.error('Error creating business progress:', error);
      throw error;
    }
  }
  
  async updateBusinessProgress(id: string, data: Partial<Omit<BusinessProgress, "id" | "created_at" | "updated_at">>): Promise<BusinessProgress> {
    try {
      console.log('Updating business progress with id:', id, 'and data:', data);
      
      const { data: updatedProgress, error } = await businessProgress()
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error('Update error details:', error);
        throw error;
      }
      
      return {
        id: updatedProgress.id,
        business_name: updatedProgress.business_name,
        development_stage: updatedProgress.development_stage as "development" | "launch" | "growth",
        description: updatedProgress.description,
        industry_type: updatedProgress.industry_type as string | null,
        user_id: updatedProgress.user_id,
        created_at: updatedProgress.created_at,
        updated_at: updatedProgress.updated_at
      };
    } catch (error) {
      console.error('Error updating business progress:', error);
      throw error;
    }
  }
  
  async deleteBusinessProgress(id: string): Promise<void> {
    try {
      console.log('Deleting business progress with id:', id);
      
      const { error } = await businessProgress()
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Delete error details:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error deleting business progress:', error);
      throw error;
    }
  }
}

export default new BusinessProgressService();
